import { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'; // eslint-disable-line no-unused-vars
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  FileText,
  Briefcase,
  Receipt,
  FileSpreadsheet,
  Package,
  ChevronRight,
  Wrench,
  ShieldCheck,
  Settings,
  FlaskConical,
  ClipboardList,
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import SidebarFooter from './SidebarFooter';
import { cn } from '../utils/cn';
import companyLogo from '../assets/company-logo.png';
import { CEMENT_TYPES } from '../pages/OpcTest/cementTypes';

// ── localStorage helpers — sidebar group open/close persistence ──────────────
const GROUPS_STORAGE_KEY = 'cts.sidebar.groups';

function readGroupsMap() {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeGroupsMap(map) {
  try {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // storage unavailable — silently continue
  }
}

// Read a single group's stored state (null = no stored value).
function readGroupOpen(name) {
  const map = readGroupsMap();
  return Object.prototype.hasOwnProperty.call(map, name) ? map[name] : null;
}

// Write a single group's state into the map.
function writeGroupOpen(name, value) {
  const map = readGroupsMap();
  writeGroupsMap({ ...map, [name]: value });
}

const navigationGroups = [
  {
    title: 'Overview',
    items: [
      { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    ]
  },
  {
    title: 'Operations',
    items: [
      { name: 'Customers', path: '/customers', icon: Users },
      { name: 'Inquiries', path: '/enquiries', icon: MessageSquare },
      { name: 'Quotations', path: '/quotations', icon: FileText },
      { name: 'Service Requests', path: '/service-request-forms', icon: Briefcase },
      { name: 'Job Cards', path: '/job-cards', icon: Briefcase },
    ]
  },
  {
    title: 'Billing & Logistics',
    items: [
      { name: 'Proforma Invoices', path: '/proforma-invoices', icon: Receipt },
      { name: 'Invoices', path: '/invoices', icon: FileSpreadsheet },
      { name: 'Material Scope', path: '/materials', icon: Package },
    ]
  },
  {
    title: 'Test Execution',
    items: [
      {
        name: 'Materials Testing',
        icon: FlaskConical,
        permission: 'MATERIAL_TESTING_ACCESS',
        children: [
          { name: 'Concrete Cube', path: '/test-execution/cube', permission: 'MATERIAL_TESTING_ACCESS' },
          {
            name: 'Cement',
            permission: 'MATERIAL_TESTING_ACCESS',
            children: CEMENT_TYPES.map((ct) => ({
              name: ct.displayName,
              path: `/test-execution/cement/${ct.slug}`,
              permission: 'MATERIAL_TESTING_ACCESS',
            })),
          },
          {
            name: 'HSDB Steel',
            permission: 'MATERIAL_TESTING_ACCESS',
            children: [
              { name: 'Steel Bar Tests', path: '/test-execution/steel', permission: 'MATERIAL_TESTING_ACCESS' },
              { name: 'Guide', path: '/test-execution/steel/guide', permission: 'MATERIAL_TESTING_ACCESS' },
            ],
          },
        ],
      },
      { name: 'My Assignments', path: '/my-assignments', icon: ClipboardList },
    ]
  },
  {
    title: 'System',
    items: [
      { name: 'Utility', path: '/utility', icon: Wrench },
    ]
  },
  {
    title: 'Administration',
    items: [
      { name: 'Administration', path: '/admin', icon: ShieldCheck },
      { name: 'Tax Settings', path: '/admin/tax-settings', icon: Settings },
    ]
  }
];

// Paths visible only when the user's role is one of these. Bypasses ITEM_PERMISSIONS.
const ROLE_GATED_PATHS = {
  '/admin': new Set(['CEO']),
  '/admin/tax-settings': new Set(['ADMIN', 'CEO']),
};

// Module-access permission code required to see each path (missing entry = no
// restriction). These are the new MODULE_ACCESS codes that the role-config
// wizard manages — gating on them makes the wizard / per-user overrides
// authoritative for what appears in the rail. (V122 granted every role that
// held a legacy ACCESS_* code its new equivalent, so nothing currently allowed
// disappears.) CEO / ADMIN bypass all gates below.
const ITEM_PERMISSIONS = {
  '/customers':              'CUSTOMER_ACCESS',
  '/enquiries':              'ENQUIRY_ACCESS',
  '/quotations':             'QUOTATION_ACCESS',
  '/service-request-forms':  'SRF_ACCESS',
  '/job-cards':              'JOB_CARD_ACCESS',
  '/proforma-invoices':      'PROFORMA_INVOICE_ACCESS',
  '/invoices':               'FINAL_INVOICE_ACCESS',
  '/materials':              'MATERIAL_SCOPE_ACCESS',
  '/test-execution/cube':    'MATERIAL_TESTING_ACCESS',
  ...Object.fromEntries(
    CEMENT_TYPES.map((ct) => [`/test-execution/cement/${ct.slug}`, 'MATERIAL_TESTING_ACCESS'])
  ),
  '/test-execution/steel':       'MATERIAL_TESTING_ACCESS',
  '/test-execution/steel/guide': 'MATERIAL_TESTING_ACCESS',
  '/my-assignments': 'MY_ASSIGNMENT_ACCESS',
};

// Paths only CEO may access — the unified /admin console is visible to any
// admin; the rail inside it hides CEO-only sections for non-CEO users.
const CEO_ONLY_PATHS = new Set();

// ── INSTRUMENT console tokens (theme-following) ──────────────────────────────
// Light: paper surface, ink text, garnet accent. Dark: graphite console, white
// text, emerald accent. The measurement-spine + needle motif persists in both.
const ACCENT_TEXT = 'text-primary-600 dark:text-carbon-400';
const ACCENT_BAR  = 'bg-primary-500 dark:bg-carbon-400';
const ROW_INACTIVE =
  'text-slate-700 dark:text-white/75 hover:text-slate-900 dark:hover:text-white ' +
  'hover:bg-slate-100 dark:hover:bg-white/[0.06] border-transparent';
const ICON_INACTIVE = 'text-slate-500 dark:text-white/65 group-hover:text-slate-700 dark:group-hover:text-white/90';
const PANEL = 'bg-white dark:bg-[#16181f] border border-slate-200 dark:border-white/15';
// Keyboard focus ring — inset so the nav's overflow-x-hidden never clips it.
// The global `*:focus-visible` outline only covers button/input/select/textarea,
// so NavLink (<a>) rows would otherwise show no focus indicator at all.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ' +
  'focus-visible:ring-primary-500 dark:focus-visible:ring-carbon-400';
// Subtle press feedback for toggle buttons (skipped under reduced-motion).
const PRESS = 'transition-transform active:scale-[0.985] motion-reduce:active:scale-100';

// Left-pad per nesting depth (literal strings so Tailwind JIT keeps them).
const INDENT = ['pl-5', 'pl-11', 'pl-[3.75rem]', 'pl-16'];
const indentClass = (depth) => INDENT[Math.min(depth, INDENT.length - 1)];

// Stable id for a disclosure submenu, linking its toggle's aria-controls to the
// <ul> it expands (W3C Disclosure Navigation pattern).
const submenuId = (name) => `nav-sub-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

// True when the current path matches this item, or (recursively) any descendant leaf.
function isItemActive(item, pathname) {
  if (item.path) {
    return pathname === item.path || pathname.startsWith(item.path + '/');
  }
  return (item.children || []).some((child) => isItemActive(child, pathname));
}

const sidebarVariants = {
  expanded: { width: 268 },
  collapsed: { width: 72 },
};

const childListVariants = {
  open:   { height: 'auto', opacity: 1 },
  closed: { height: 0,      opacity: 0 },
};

// Measurement-spine tick gradients (ink in light mode, light in dark mode).
const SPINE_LIGHT = {
  backgroundImage:
    'repeating-linear-gradient(to bottom, rgba(15,23,42,0.10) 0 1px, transparent 1px 8px),' +
    'repeating-linear-gradient(to bottom, rgba(15,23,42,0.22) 0 1px, transparent 1px 40px)',
  backgroundSize: '6px 100%, 11px 100%',
  backgroundRepeat: 'no-repeat',
};
const SPINE_DARK = {
  backgroundImage:
    'repeating-linear-gradient(to bottom, rgba(255,255,255,0.07) 0 1px, transparent 1px 8px),' +
    'repeating-linear-gradient(to bottom, rgba(255,255,255,0.16) 0 1px, transparent 1px 40px)',
  backgroundSize: '6px 100%, 11px 100%',
  backgroundRepeat: 'no-repeat',
};

export default function Sidebar({ isCollapsed, onToggle, isMobile = false }) {
  const location = useLocation();
  const { role, has } = usePermissions();
  const [hoveredItem, setHoveredItem] = useState(null);
  const reduceMotion = useReducedMotion();

  // effectiveExpanded drives width + label/header visibility.
  // Width changes only via the explicit toggle so the header + main area
  // (which key off isCollapsed) always stay in sync with the rail.
  const effectiveExpanded = !isCollapsed;

  const isPathAllowed = useMemo(() => (path) => {
    if (CEO_ONLY_PATHS.has(path)) return role === 'CEO';
    const gated = ROLE_GATED_PATHS[path];
    if (gated) return gated.has(role);
    if (role === 'CEO' || role === 'ADMIN') return true;
    const perm = ITEM_PERMISSIONS[path];
    if (!perm) return true;
    // Support both string and [newStyleCode, legacyCode] array forms
    if (Array.isArray(perm)) {
      return perm.some((code) => has(code));
    }
    return has(perm);
  }, [role, has]);

  // Recursive: a parent is allowed when it passes its own permission gate and
  // has at least one visible descendant; a leaf is allowed by its path.
  const isItemAllowed = useMemo(() => {
    const check = (item) => {
      if (item.children) {
        if (item.permission && role !== 'CEO' && role !== 'ADMIN' && !has(item.permission)) return false;
        return item.children.some((child) => check(child));
      }
      return isPathAllowed(item.path);
    };
    return check;
  }, [role, has, isPathAllowed]);

  const filteredGroups = useMemo(() => {
    return navigationGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => isItemAllowed(item)),
      }))
      .filter((group) => group.items.length > 0);
  }, [isItemAllowed]);

  return (
    <motion.aside
      initial={effectiveExpanded ? "expanded" : "collapsed"}
      animate={effectiveExpanded ? "expanded" : "collapsed"}
      variants={sidebarVariants}
      transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 250, damping: 28 }}
      aria-label="Main Navigation"
      className={cn(
        "fixed left-0 top-0 h-full z-30 flex flex-col",
        "bg-white dark:bg-[#0d0e12] border border-slate-300 dark:border-white/15"
      )}
    >
      {/* Header / Logo — instrument nameplate */}
      <div className="relative h-[72px] flex items-center px-4 border-b border-slate-200 dark:border-white/10 overflow-hidden">
        <div className="flex items-center gap-3 min-w-max">
          <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-slate-100 dark:bg-white/[0.04] ring-1 ring-slate-200 dark:ring-white/10">
            <img
              src={companyLogo}
              alt="CivilTech company logo"
              className="w-9 h-9 object-contain"
              draggable={false}
            />
          </div>
          <AnimatePresence mode="wait">
            {effectiveExpanded && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={reduceMotion ? { duration: 0 } : undefined}
                className="flex flex-col"
              >
                <span className="font-serif text-[22px] font-bold text-slate-900 dark:text-white leading-none tracking-tight">
                  CivilTech
                </span>
                <span className={cn("font-ledger-mono text-xs tracking-[0.16em] uppercase mt-1.5", ACCENT_TEXT)}>
                  ERP / Lab Ops
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation — the measurement spine rides the left edge */}
      <nav className="rail-scroll relative flex-1 py-3 overflow-y-auto overflow-x-hidden">
        {/* Measurement spine: minor ticks every 8px, major every 40px */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-3 dark:hidden" aria-hidden style={SPINE_LIGHT} />
        <div className="pointer-events-none absolute inset-y-0 left-0 w-3 hidden dark:block" aria-hidden style={SPINE_DARK} />

        {filteredGroups.map((group, groupIdx) => (
          <div key={group.title} className={cn(groupIdx > 0 && "mt-3 pt-3")}>
            {effectiveExpanded ? (
              <div className="flex items-baseline gap-2 px-5 py-2">
                <span className="font-ledger-mono text-xs font-bold text-slate-500 dark:text-white/50 tabular-nums">
                  {String(groupIdx + 1).padStart(2, '0')}
                </span>
                <h3 className="font-ledger-mono text-xs font-bold tracking-[0.14em] uppercase text-slate-600 dark:text-white/70">
                  {group.title}
                </h3>
                <span className="ml-2 flex-1 h-px bg-slate-200 dark:bg-white/10" aria-hidden />
              </div>
            ) : (
              groupIdx > 0 && <div className="mx-4 my-3 border-t border-slate-200 dark:border-white/10" />
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                // Collapsible parent item (one or more nesting levels)
                if (item.children) {
                  return (
                    <CollapsibleParent
                      key={item.name}
                      item={item}
                      depth={0}
                      isCollapsed={!effectiveExpanded}
                      location={location}
                      setHoveredItem={setHoveredItem}
                      isItemAllowed={isItemAllowed}
                    />
                  );
                }

                // Flat item
                const Icon = item.icon;
                const isActive = location.pathname === item.path ||
                  (item.path !== '/' && location.pathname.startsWith(item.path + '/'));
                const isHovered = hoveredItem === item.name;

                return (
                  <li key={item.name}>
                    <NavLink
                      to={item.path}
                      onMouseEnter={() => setHoveredItem(item.name)}
                      onMouseLeave={() => setHoveredItem(null)}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        "relative group transition-colors",
                        FOCUS_RING,
                        "flex items-center gap-3 pl-5 pr-4 py-2.5 font-ledger-mono text-sm tracking-wide border-l-2",
                        isActive
                          ? cn("bg-slate-100 dark:bg-white/[0.06] border-l-current font-medium", ACCENT_TEXT)
                          : ROW_INACTIVE
                      )}
                    >
                      <Icon
                        size={18}
                        className={cn("flex-shrink-0 transition-colors", isActive ? ACCENT_TEXT : ICON_INACTIVE)}
                      />
                      {effectiveExpanded && <span className="truncate flex-1">{item.name}</span>}
                      {effectiveExpanded && isActive && (
                        <span className={cn("w-1.5 h-1.5 flex-shrink-0", ACCENT_BAR)} aria-hidden />
                      )}
                      {!effectiveExpanded && isHovered && <RailTooltip label={item.name} />}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer — theme selector + sign-out, pinned below the scroll area */}
      <SidebarFooter isCollapsed={!effectiveExpanded} />

      {/* Rail toggle — precision detent on the console edge */}
      {!isMobile && (
        <button
          onClick={onToggle}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn(
            "absolute -right-3 top-20 z-40 w-6 h-10 flex items-center justify-center transition-colors",
            "bg-white dark:bg-[#0d0e12] text-slate-600 dark:text-white/70 border border-slate-300 dark:border-white/15",
            "hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900 active:scale-95",
            FOCUS_RING
          )}
        >
          <motion.div
            animate={{ rotate: isCollapsed ? 0 : 180 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <ChevronRight className="w-4 h-4" />
          </motion.div>
        </button>
      )}
    </motion.aside>
  );
}

/** Flat tooltip shown beside an icon in rail-collapsed mode. */
function RailTooltip({ label }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      role="tooltip"
      className={cn(
        "absolute left-full ml-3 px-3 py-1.5 text-xs font-ledger-mono uppercase tracking-[0.14em] font-bold whitespace-nowrap z-50",
        "text-slate-900 dark:text-white shadow-lg", PANEL
      )}
    >
      {label}
    </motion.div>
  );
}

/**
 * CollapsibleParent — expandable nav item, recursive to any depth.
 * Children with their own `children` render as nested CollapsibleParents; leaf
 * children render as NavLinks. Self-manages open state (default follows the
 * active section; explicit toggle overrides). In rail-collapsed mode the parent
 * shows an icon plus a hover flyout exposing the full nested tree.
 */
function CollapsibleParent({ item, depth, isCollapsed, location, setHoveredItem, isItemAllowed }) {
  const Icon = item.icon;
  const active = isItemActive(item, location.pathname);
  const reduce = useReducedMotion();
  const [override, setOverride] = useState(() => readGroupOpen(item.name));
  const open = override !== null ? override : active;
  const toggleOpen = () => {
    const next = !open;
    setOverride(next);
    writeGroupOpen(item.name, next);
  };

  const visibleChildren = item.children.filter((child) => isItemAllowed(child));

  // Rail-collapsed: icon + hover flyout with the full nested tree (top-level only).
  if (isCollapsed) {
    if (depth > 0) return null;
    return (
      <li className="relative group/rail">
        <button
          type="button"
          onMouseEnter={() => setHoveredItem(item.name)}
          onMouseLeave={() => setHoveredItem(null)}
          aria-label={item.name}
          className={cn(
            "relative w-full flex items-center justify-center pl-5 pr-4 py-2.5 border-l-2 transition-colors",
            FOCUS_RING, PRESS,
            active
              ? cn("bg-slate-100 dark:bg-white/[0.06] border-l-current", ACCENT_TEXT)
              : "border-transparent hover:bg-slate-100 dark:hover:bg-white/[0.04]"
          )}
        >
          {Icon && (
            <Icon size={18} className={cn("flex-shrink-0 transition-colors", active ? ACCENT_TEXT : ICON_INACTIVE)} />
          )}
        </button>

        {/* Hover flyout — bridges the rail gap so nested items stay reachable */}
        <div className="invisible opacity-0 group-hover/rail:visible group-hover/rail:opacity-100 transition-opacity absolute left-full top-0 pl-2 z-50">
          <div className={cn("min-w-[228px] origin-left scale-95 py-2 shadow-2xl transition-transform duration-150 ease-out-expo group-hover/rail:scale-100 motion-reduce:transform-none motion-reduce:transition-none", PANEL)}>
            <div className={cn("px-4 pb-2 mb-1 border-b border-slate-200 dark:border-white/10 font-ledger-mono text-xs font-bold uppercase tracking-[0.14em]", ACCENT_TEXT)}>
              {item.name}
            </div>
            <ul>
              {visibleChildren.map((child) => (
                <FlyoutNode key={child.path || child.name} node={child} location={location} isItemAllowed={isItemAllowed} />
              ))}
            </ul>
          </div>
        </div>
      </li>
    );
  }

  // Expanded: toggle row + animated child list.
  return (
    <li>
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-controls={submenuId(item.name)}
        className={cn(
          "relative w-full group transition-colors flex items-center gap-3 pr-4 border-l-2",
          FOCUS_RING, PRESS,
          indentClass(depth),
          depth === 0 ? "py-2.5 font-ledger-mono text-sm tracking-wide" : "py-2 font-ledger-mono text-[13px]",
          active
            ? cn("bg-slate-100 dark:bg-white/[0.06] border-l-current font-medium", ACCENT_TEXT)
            : ROW_INACTIVE
        )}
      >
        {depth === 0 && Icon && (
          <Icon size={18} className={cn("flex-shrink-0 transition-colors", active ? ACCENT_TEXT : ICON_INACTIVE)} />
        )}
        <span className="truncate flex-1 text-left">{item.name}</span>
        <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ duration: reduce ? 0 : 0.2 }} className="flex-shrink-0">
          <ChevronRight size={14} className="text-slate-500 dark:text-white/60" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.ul
            key="children"
            initial="closed"
            animate="open"
            exit="closed"
            variants={childListVariants}
            transition={{ duration: reduce ? 0 : 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
            id={submenuId(item.name)}
          >
            {visibleChildren.map((child) =>
              child.children ? (
                <CollapsibleParent
                  key={child.name}
                  item={child}
                  depth={depth + 1}
                  isCollapsed={isCollapsed}
                  location={location}
                  setHoveredItem={setHoveredItem}
                  isItemAllowed={isItemAllowed}
                />
              ) : (
                <LeafChild key={child.path} child={child} depth={depth + 1} location={location} />
              )
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}

/** LeafChild — a terminal navigable row inside a CollapsibleParent. */
function LeafChild({ child, depth, location }) {
  const isChildActive = location.pathname === child.path ||
    location.pathname.startsWith(child.path + '/');
  return (
    <li>
      <NavLink
        to={child.path}
        aria-current={isChildActive ? 'page' : undefined}
        className={cn(
          "flex items-center gap-2 pr-4 py-2 border-l-2 transition-colors font-ledger-mono text-[13px]",
          FOCUS_RING,
          indentClass(depth),
          isChildActive
            ? cn("bg-slate-100 dark:bg-white/[0.06] border-l-current font-medium", ACCENT_TEXT)
            : ROW_INACTIVE
        )}
      >
        <span className="truncate">{child.name}</span>
        {isChildActive && <span className={cn("ml-auto w-1.5 h-1.5 flex-shrink-0", ACCENT_BAR)} aria-hidden />}
      </NavLink>
    </li>
  );
}

/** FlyoutNode — recursively renders a nav node inside the rail hover flyout (always fully expanded). */
function FlyoutNode({ node, location, isItemAllowed }) {
  if (node.children) {
    const kids = node.children.filter((child) => isItemAllowed(child));
    return (
      <li>
        <div className="px-4 pt-2 pb-1 font-ledger-mono text-xs font-bold uppercase tracking-[0.14em] text-slate-600 dark:text-white/70">
          {node.name}
        </div>
        <ul>
          {kids.map((child) => (
            <FlyoutNode key={child.path || child.name} node={child} location={location} isItemAllowed={isItemAllowed} />
          ))}
        </ul>
      </li>
    );
  }

  const isActive = location.pathname === node.path || location.pathname.startsWith(node.path + '/');
  return (
    <li>
      <NavLink
        to={node.path}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          "flex items-center gap-2 px-4 py-2 font-ledger-mono text-[13px] transition-colors border-l-2",
          FOCUS_RING,
          isActive
            ? cn("bg-slate-100 dark:bg-white/[0.06] border-l-current font-medium", ACCENT_TEXT)
            : ROW_INACTIVE
        )}
      >
        <span className="truncate">{node.name}</span>
        {isActive && <span className={cn("ml-auto w-1.5 h-1.5 flex-shrink-0", ACCENT_BAR)} aria-hidden />}
      </NavLink>
    </li>
  );
}
